import { useEffect, useRef, useState } from 'react';
import { CameraPreview } from '@capacitor-community/camera-preview';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import { VideoEditor } from '@whiteguru/capacitor-plugin-video-editor';
import { Flashlight, FlashlightOff } from 'lucide-react';
import BusyIndicator from '@/components/BusyIndicator';

interface Props {
    open: boolean;
    onClose: () => void;
    onCapture: (file: File) => void;
}

export default function CameraPreviewModal({ open, onClose, onCapture }: Props) {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [duration, setDuration] = useState(0);
    const [flashOn, setFlashOn] = useState(false);
    const [videoMode, setVideoMode] = useState(false);

    const pressTimer = useRef<any>(null);
    const recordingInterval = useRef<any>(null);

    // הגבלת זמן מקסימלית (Max Duration) ב-שניות. כאן הגדרנו דקה אחת.
    const MAX_RECORDING_SECONDS = 60;

    // האזנה לטיימר: ברגע שמגיעים למקסימום, עוצרים את ההקלטה (Auto-stop)
    useEffect(() => {
        if (isRecording && duration >= MAX_RECORDING_SECONDS) {
            console.log('Max recording limit reached. Stopping automatically.');
            endPress(); // קורא לפונקציית העצירה בדיוק כאילו המשתמש עזב את הכפתור
        }
    }, [duration, isRecording]);

    useEffect(() => {
        if (isRecording) {
            setDuration(0);
            recordingInterval.current = setInterval(() => {
                setDuration((prev) => prev + 1);
            }, 1000);
        } else {
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
            }
        }

        return () => {
            if (recordingInterval.current) clearInterval(recordingInterval.current);
        };
    }, [isRecording]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (open && Capacitor.isNativePlatform()) {
            // 1. הריגת הצבע של ה-body וה-html בצורה אגרסיבית
            document.body.style.setProperty('background-color', 'transparent', 'important');
            document.body.style.setProperty('background', 'transparent', 'important');
            document.documentElement.style.setProperty('background-color', 'transparent', 'important');

            // 2. העלמה מוחלטת של כל האפליקציה (הדיב שצבוע ב-HSL הזה)
            const rootApp = document.getElementById('root');
            if (rootApp) {
                rootApp.style.setProperty('display', 'none', 'important');
            }

            navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                .then((stream) => {
                    // ברגע שיש אישור, אפשר לסגור את הסטרים של הדפדפן, הנייטיב כבר קיבל אישור
                    stream.getTracks().forEach(track => track.stop());
                    startCamera();
                })
                .catch((err) => {
                    console.error('Microphone permission denied', err);
                    // כאן אתה יכול להקפיץ הודעה (Toast) למשתמש שהוא חייב לאשר מיקרופון
                });
            startCamera();
        }

        return () => {
            // 3. החזרת המצב לקדמותו כשהמצלמה נסגרת
            document.body.style.removeProperty('background-color');
            document.body.style.removeProperty('background');
            document.documentElement.style.removeProperty('background-color');

            const rootApp = document.getElementById('root');
            if (rootApp) {
                rootApp.style.removeProperty('display');
            }

            stopCamera();
        };
    }, [open]);

    const toggleFlash = async () => {
        const next = !flashOn;

        try {
            await CameraPreview.setFlashMode({
            flashMode: next ? 'on' : 'off',
            });

            setFlashOn(next);
        } catch (err) {
            console.error('Failed to toggle flash', err);
        }
    };

    const startCamera = async () => {
        await CameraPreview.start({
            parent: 'cameraPreview',
            className: 'camera-preview',
            position: 'rear',
            toBack: true,
        });
    };

    const stopCamera = async () => {
        try {
            await CameraPreview.stop();
        } catch { }
    };

    const takePhoto = async () => {
        const result = await CameraPreview.capture({
            quality: 90,
        });

        const base64 = `data:image/jpeg;base64,${result.value}`;
        const file = base64ToFile(base64, 'photo.jpg');

        onCapture(file);
        onClose();
    };

    // 🔥 לחיצה ארוכה = וידאו (placeholder)
    const startPress = () => {
        pressTimer.current = setTimeout(async () => {
            setIsRecording(true);
            console.log('Start recording...');
            try {
                // מתחיל הקלטת וידאו ברמת הנייטיב (Native video recording)
                await CameraPreview.startRecordVideo({
                    position: 'rear',
                    width: 1280, // הגבלת רזולוציה ל-720p (Resolution limit)
                    height: 720
                });
            } catch (error) {
                console.error('Error starting video record:', error);
                setIsRecording(false);
            }
        }, 500);
    };

    // con
    const endPress = async () => {
        clearTimeout(pressTimer.current);

        if (isRecording) {
            setIsRecording(false);
            try {
                // 1. מדליקים מסך טעינה למשתמש (שלא יחשוב שהאפליקציה נתקעה)
                setIsProcessing(true);
                

                // 2. עוצרים את ההקלטה ומקבלים את הקובץ הענק (ה-200MB)
                const result = await CameraPreview.stopRecordVideo() as unknown as { videoFilePath: string };
                let finalVideoPath = result.videoFilePath;

                try {
                    // 3. הלב של המערכת - דוחסים את הוידאו ל-720p!
                    console.log('מתחיל דחיסת וידאו...');
                    const transcodeResult = await VideoEditor.edit({
                        path: finalVideoPath,
                        transcode: {
                            width: 720,  // כופה רוחב קטן יותר
                            height: 1280, // כופה גובה קטן יותר
                            fps: 30,      // מוריד קצב פריימים סטנדרטי
                            keepAspectRatio: true
                        }
                    });

                    // שולפים את הנתיב החדש של הוידאו המכווץ
                    finalVideoPath = transcodeResult.file?.path || result.videoFilePath;
                    console.log('הדחיסה הסתיימה בהצלחה!');
                } catch (compressErr) {
                    // במקרה קיצון שהדחיסה נכשלה, לפחות יש לנו את הקובץ המקורי (Fallback)
                    console.error('שגיאה בדחיסת הוידאו:', compressErr);
                }

                // 4. ממירים את הנתיב הסופי (הדחוס) לכתובת Web כדי שנוכל להעלות
                const webPath = Capacitor.convertFileSrc(finalVideoPath);

                // 5. שואבים את הקובץ
                const response = await fetch(webPath);
                const blob = await response.blob();

                // 6. יוצרים את ה-File שיילך לשרת
                const file = new File([blob], `video_${Date.now()}.mp4`, { type: 'video/mp4' });

                console.log(`🎥 גודל סופי לשליחה: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);

                // 7. מכבים מסך טעינה ומעבירים הלאה
                setIsProcessing(false);
                onCapture(file);
                onClose();

            } catch (error) {
                console.error('Error stopping video record:', error);
                setIsProcessing(false);
            }
        } else {
            // takePhoto();
        }
    };

    const base64ToFile = (base64: string, filename: string): File => {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    };

    if (!open) return null;

    return createPortal(
        <div className="camera-ui-wrapper fixed inset-0 z-[99999]" style={{ pointerEvents: 'none' }}>
            <div id="cameraPreview" className="absolute inset-0" style={{ zIndex: -1 }} />
            <div className="relative w-full h-full" style={{ pointerEvents: 'auto' }}>
                {isProcessing && <BusyIndicator text="מעבד וידאו..." />}
                {isRecording && (
                    <div className="absolute top-12 left-0 right-0 flex justify-center pointer-events-none">
                        <div className="bg-red-500/90 text-white px-4 py-1.5 rounded-full font-mono text-lg shadow-lg border border-white/20 flex items-center gap-3">
                            <span className="w-2.5 h-2.5 bg-white rounded-full animate-pulse" />
                            <span>
                                {formatTime(duration)} <span className="text-white/60">/ {formatTime(MAX_RECORDING_SECONDS)}</span>
                            </span>
                        </div>
                    </div>
                )}
                <button
                    onClick={onClose}
                    className="absolute top-12 right-6 text-white text-4xl font-bold p-4"
                    style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
                >
                    ✕
                </button>
                <button
                    onClick={toggleFlash}
                    className="absolute top-12 left-6 text-white p-4 rounded-full bg-black/30"
                    style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
                    >
                    {flashOn ? <Flashlight size={32} /> : <FlashlightOff size={32} />}
                </button>
                <div className="absolute bottom-20 left-0 right-0 flex justify-center">
                    {videoMode ? <div
                        onMouseDown={startPress}
                        onMouseUp={endPress}
                        onTouchStart={startPress}
                        onTouchEnd={endPress}
                        className={`w-24 h-24 rounded-full border-8 border-white shadow-2xl ${isRecording ? 'bg-red-500 scale-110' : 'bg-transparent'
                            } transition-all`}
                    /> : <div
                        onClick={takePhoto}
                        className="w-24 h-24 rounded-full border-8 border-white shadow-2xl bg-transparent transition-all"
                    /> }
                </div>
                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4">
                    <div className="inline-flex rounded-full bg-white p-1 shadow-lg">
                        <button
                            type="button"
                            onClick={() => setVideoMode(false)}
                            disabled={isRecording || isProcessing}
                            className={`px-5 py-2 rounded-full transition-colors text-sm font-medium ${videoMode ? 'bg-transparent text-black/70 hover:text-black' : 'bg-black text-white'}`}
                        >
                            צילום
                        </button>
                        <button
                            type="button"
                            onClick={() => setVideoMode(true)}
                            disabled={isRecording || isProcessing}
                            className={`px-5 py-2 rounded-full transition-colors text-sm font-medium ${videoMode ? 'bg-black text-white' : 'bg-transparent text-black/70 hover:text-black'}`}
                        >
                            וידאו
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body // שולח את המצלמה ישירות לסוף ה-Body
    );
}